/**
 * The sketch ↔ the API (pure, tested in roofModel.test.ts):
 * - `fromRoofModel`: a version's roof model (a Sketch's, or an automatic design's detected roof) as a sketch;
 * - `toSketchRoof`: what a save or a preview sends (`SketchRoof`);
 * - `toRoofModel`: the roof model the server will build from it (app/design/sketch/roof.py, same rules), for the
 *   live 2D/3D views, panel placement and checks before saving. Unchanged faces keep the base record (a detected
 *   face stays detected); changed faces keep their uv origin in plan; new faces start at their anchor.
 */
import type { S } from "../api/client";
import { normalFrom, uvAxes, type Vec2, type Vec3 } from "../editor/geo.ts";
import { dormerGeometry, type SkDormer } from "./dormers.ts";
import { anchorIndex, planeZ, resolveSlopes, ringArea, type EdgeType, type FaceSettings, type Sketch, type SkFace, type SkStructure } from "./skeleton.ts";

type RoofModel = S["RoofModelOutput"];
const EDGE_TYPES: EdgeType[] = ["eave", "rake", "ridge", "hip", "valley"];
const POSITION_EPS = 1e-3;
const ANGLE_EPS = 0.01;

/** A version's roof model as a sketch (faces as they are, not found again, so a detected roof loads exactly). */
export function fromRoofModel(model: RoofModel | null | undefined): Sketch {
  if (!model) return { vertices: {}, edges: [], faces: [] };
  // a dormer's faces and the hole it cuts are made by the server from the dormer: the sketch keeps the dormer
  const dormerFaces = model.faces.filter((f) => f.dormer_id);
  const own = new Set(model.faces.filter((f) => !f.dormer_id).flatMap((f) => f.boundary));
  const dormerOnly = new Set([...dormerFaces.flatMap((f) => f.boundary), ...model.faces.flatMap((f) => (f.holes ?? []).flat())].filter((v) => !own.has(v)));
  const vertices: Record<string, Vec2> = Object.fromEntries(model.vertices.filter((v) => !dormerOnly.has(v.id)).map((v) => [v.id, [v.xyz[0]!, v.xyz[1]!] as Vec2]));
  const z = Object.fromEntries(model.vertices.map((v) => [v.id, v.xyz[2]!]));
  const edges = model.edges.filter((e) => e.v.every((v) => !dormerOnly.has(v))).map((e) => ({
    id: e.id,
    a: e.v[0]!,
    b: e.v[1]!,
    type: (EDGE_TYPES as string[]).includes(e.type) ? (e.type as EdgeType) : e.faces.length > 1 ? "hip" : "rake",
  }));
  const faces: SkFace[] = model.faces.filter((f) => !f.dormer_id).map((f) => ({
    id: f.id,
    boundary: [...f.boundary],
    pitch: f.plane.pitch_deg,
    azimuth: f.plane.azimuth_deg,
    eaveHeight: Math.min(...f.boundary.map((v) => z[v] ?? 0)),
    label: f.label,
    structureId: f.structure_id ?? null,
    pitchSource: f.pitch_source ?? null,
  }));
  const drawn = model.sketch ?? {};
  const named = new Map((model.structures ?? []).map((x) => [x.id, x]));
  const structures: SkStructure[] = (drawn.structures ?? []).map((x) => structureFrom(x));
  for (const [id, x] of named) if (!structures.some((y) => y.id === id)) structures.push({ id, name: x.name, kind: (x.kind as SkStructure["kind"]) ?? (id === "main" ? "house" : "other"), eaveHeight: x.eave_height_m ?? 3 });
  const dormers: SkDormer[] = (drawn.dormers ?? []).map((d) => ({ id: d.id, faceId: d.face_id, kind: d.kind, center: [d.center[0]!, d.center[1]!], width: d.width_m, depth: d.depth_m, height: d.height_m, pitch: d.pitch_deg }));
  return { vertices, edges, faces, ...(structures.length ? { structures } : {}), ...(dormers.length ? { dormers } : {}) };
}

function structureFrom(x: S["SketchStructure"]): SkStructure {
  return {
    id: x.id,
    name: x.name,
    kind: x.kind ?? (x.id === "main" ? "house" : "other"),
    eaveHeight: x.eave_height_m ?? 3,
    hidden: x.hidden ?? false,
    footprint: x.footprint ? { ring: x.footprint.ring.map((p) => [p[0]!, p[1]!] as Vec2), edges: x.footprint.edges.map((e) => ({ mode: e.mode, pitch: e.pitch_deg })) } : null,
  };
}

/** The structures and dormers as the API takes them. */
export function sketchExtras(s: Sketch): Pick<S["SketchRoof"], "structures" | "dormers"> {
  const out: Pick<S["SketchRoof"], "structures" | "dormers"> = {};
  if (s.structures?.length)
    out.structures = s.structures.map((x) => ({
      id: x.id,
      name: x.name.slice(0, 60) || "Building",
      kind: x.kind,
      eave_height_m: round(x.eaveHeight),
      ...(x.hidden ? { hidden: true } : {}),
      footprint: x.footprint ? { ring: x.footprint.ring.map((p) => [round(p[0]), round(p[1])]), edges: x.footprint.edges.map((e) => ({ mode: e.mode, pitch_deg: round2(e.pitch) })) } : null,
    }));
  const faces = new Set(s.faces.map((f) => f.id));
  const dormers = (s.dormers ?? []).filter((d) => faces.has(d.faceId));
  if (dormers.length)
    out.dormers = dormers.map((d) => ({ id: d.id, face_id: d.faceId, kind: d.kind, center: [round(d.center[0]), round(d.center[1])], width_m: round2(d.width), depth_m: round2(d.depth), height_m: round2(d.height), pitch_deg: round2(d.pitch) }));
  return out;
}

/** The faces' slopes as the API receives them (azimuth from the eave where there is one). */
export function toSketchRoof(s: Sketch): S["SketchRoof"] {
  const slopes = resolveSlopes(s);
  const used = new Set(s.faces.flatMap((f) => f.boundary));
  for (const e of s.edges) {
    used.add(e.a);
    used.add(e.b);
  }
  return {
    vertices: Object.entries(s.vertices)
      .filter(([id]) => used.has(id))
      .map(([id, xy]) => ({ id, xy: [round(xy[0]), round(xy[1])] })),
    edges: s.edges.map((e) => ({ v: [e.a, e.b], type: e.type })),
    faces: s.faces.map((f) => {
      const slope = slopes[f.id]!;
      return {
        id: f.id,
        boundary: f.boundary,
        pitch_deg: round2(slope.pitch),
        azimuth_deg: round2(((slope.azimuth % 360) + 360) % 360),
        eave_height_m: round(Math.max(0, slope.eaveHeight)),
        structure_id: f.structureId ?? null,
        label: f.label ?? null,
        ...(f.pitchSource ? { pitch_source: f.pitchSource } : {}),
      };
    }),
    ...sketchExtras(s),
  };
}

const round = (v: number) => Math.round(v * 10000) / 10000;
const round2 = (v: number) => Math.round(v * 100) / 100;

/** The roof model the server builds from `sketch` on top of `base` (for live display; the server is the truth). */
export function toRoofModel(sketch: Sketch, base: RoofModel | null | undefined): RoofModel {
  const wire = toSketchRoof(sketch);
  const plan: Record<string, Vec2> = Object.fromEntries(wire.vertices.map((v) => [v.id, v.xy as Vec2]));
  const baseFaces = new Map((base?.faces ?? []).map((f) => [f.id, f]));
  const baseVertices = new Map((base?.vertices ?? []).map((v) => [v.id, v]));
  const planes: Record<string, { normal: Vec3; d: number }> = {};
  const anchors: Record<string, Vec3> = {};
  const rings: Record<string, string[]> = {};
  const kept = new Set<string>();
  for (const f of wire.faces) {
    let ring = [...f.boundary];
    if (ringArea(ring.map((v) => plan[v]!)) < 0) ring = ring.reverse();
    rings[f.id] = ring;
    const old = baseFaces.get(f.id);
    if (old && unchanged(old, ring, f, plan, baseVertices)) {
      kept.add(f.id);
      planes[f.id] = { normal: old.plane.normal as Vec3, d: old.plane.d };
      continue;
    }
    const pts = ring.map((v) => plan[v]!);
    const normal = normalFrom(f.pitch_deg, f.azimuth_deg);
    const i = anchorIndex(pts, f.azimuth_deg);
    const anchor: Vec3 = [pts[i]![0], pts[i]![1], f.eave_height_m];
    planes[f.id] = { normal, d: -(normal[0] * anchor[0] + normal[1] * anchor[1] + normal[2] * anchor[2]) };
    anchors[f.id] = anchor;
  }
  const zOn = (id: string, [x, y]: Vec2) => -(planes[id]!.normal[0] * x + planes[id]!.normal[1] * y + planes[id]!.d) / planes[id]!.normal[2];
  const touching: Record<string, string[]> = {};
  for (const [id, ring] of Object.entries(rings)) for (const v of ring) (touching[v] ??= []).push(id);
  const vertices: RoofModel["vertices"] = Object.entries(touching).map(([id, faces]) => {
    const xy = plan[id]!;
    const old = baseVertices.get(id);
    if (old && faces.some((f) => kept.has(f)) && Math.hypot(old.xyz[0]! - xy[0], old.xyz[1]! - xy[1]) <= POSITION_EPS) return old;
    const z = faces.reduce((s, f) => s + zOn(f, xy), 0) / faces.length;
    return { id, xyz: [xy[0], xy[1], z], source: "manual", locked: true };
  });
  const faces: RoofModel["faces"] = wire.faces.map((f, n) => {
    if (kept.has(f.id)) {
      const record = baseFaces.get(f.id)!;
      return f.pitch_source && f.pitch_source !== record.pitch_source ? { ...record, pitch_source: f.pitch_source } : record;
    }
    const { normal, d } = planes[f.id]!;
    const old = baseFaces.get(f.id);
    const { u, v } = uvAxes(normal);
    const origin: Vec3 = old ? [old.uv_frame.origin[0]!, old.uv_frame.origin[1]!, zOn(f.id, [old.uv_frame.origin[0]!, old.uv_frame.origin[1]!])] : anchors[f.id]!;
    const ground = Math.abs(ringArea(rings[f.id]!.map((v) => plan[v]!)));
    return {
      id: f.id,
      label: f.label || old?.label || `Face ${n + 1}`,
      boundary: rings[f.id]!,
      plane: { normal, d, pitch_deg: f.pitch_deg, azimuth_deg: f.azimuth_deg },
      uv_frame: { origin, u, v },
      area_m2: ground / Math.max(normal[2], 1e-3),
      ground_area_m2: ground,
      mountable: true,
      structure_id: f.structure_id ?? old?.structure_id ?? null,
      source: "manual",
      locked: true,
      pitch_source: f.pitch_source ?? null,
    };
  });
  const edges: RoofModel["edges"] = [];
  const seen = new Map<string, number>();
  for (const [faceId, ring] of Object.entries(rings)) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i]!;
      const b = ring[(i + 1) % ring.length]!;
      const key = [a, b].sort().join("|");
      const at = seen.get(key);
      if (at !== undefined) {
        if (edges[at]!.faces.length < 2) edges[at]!.faces.push(faceId);
        continue;
      }
      const drawn = sketch.edges.find((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
      seen.set(key, edges.length);
      edges.push({
        id: drawn?.id ?? `s${edges.length + 1}`,
        v: [a, b],
        type: drawn?.type ?? "unknown",
        type_source: "manual",
        faces: [faceId],
        length_m: 0,
        source: "manual",
        locked: true,
      });
    }
  }
  addDormers(sketch, faces, vertices, edges);
  return {
    schema_version: "1.0",
    vertices,
    edges,
    faces,
    summary: { face_count: faces.length, area_m2: faces.reduce((s, f) => s + f.area_m2, 0), ground_area_m2: faces.reduce((s, f) => s + f.ground_area_m2, 0), edge_lengths_m: {} },
    quality: { watertight: true },
    method: { name: "sketch", version: "1" },
  };
}

function unchanged(old: RoofModel["faces"][number], ring: string[], f: S["SketchFace"], plan: Record<string, Vec2>, baseVertices: Map<string, RoofModel["vertices"][number]>): boolean {
  if ((old.holes ?? []).length || old.boundary.join(",") !== ring.join(",")) return false;
  for (const v of ring) {
    const xyz = baseVertices.get(v)?.xyz;
    if (!xyz || Math.hypot(xyz[0]! - plan[v]![0], xyz[1]! - plan[v]![1]) > POSITION_EPS) return false;
  }
  const dAz = Math.abs(((f.azimuth_deg - old.plane.azimuth_deg + 540) % 360) - 180);
  return Math.abs(old.plane.pitch_deg - f.pitch_deg) < ANGLE_EPS && (old.plane.pitch_deg < 0.5 || dAz < ANGLE_EPS);
}

/** The height of a face's plane at a plan point (live roof, for drawing). */
export function faceHeightAt(face: SkFace, s: Sketch, slope: { pitch: number; azimuth: number; eaveHeight: number }, p: Vec2): number {
  const pts = face.boundary.map((v) => s.vertices[v]!);
  return planeZ(slope.pitch, slope.azimuth, pts[anchorIndex(pts, slope.azimuth)]!, slope.eaveHeight, p);
}

/** Default settings of new faces (the pitch and eave height chips). */
export const DEFAULT_FACE: FaceSettings = { pitch: 26.57, eaveHeight: 3, azimuth: 180 };

/** Common pitches: [rise per 12 (0: flat), degrees]. x/12 rise over run, as roofers say them in North America. */
export const PITCH_CHIPS: [rise: number, deg: number][] = [
  [0, 0],
  [4, 18.43],
  [6, 26.57],
  [8, 33.69],
  [10, 39.81],
  [12, 45],
];

/** Eave heights by storeys: [storeys, metres]. */
export const STOREY_CHIPS: [storeys: number, metres: number][] = [
  [1, 3],
  [2, 6],
  [3, 9],
];

/** The dormers' faces and the holes they cut (live display; the server builds the same from the same maths). */
function addDormers(sketch: Sketch, faces: RoofModel["faces"], vertices: RoofModel["vertices"], edges: RoofModel["edges"]) {
  for (const d of sketch.dormers ?? []) {
    const host = faces.find((f) => f.id === d.faceId);
    if (!host || host.plane.pitch_deg < 5) continue;
    const g = dormerGeometry({ normal: host.plane.normal as Vec3, d: host.plane.d, azimuth: host.plane.azimuth_deg }, d);
    if (!g) continue;
    const ids: string[] = [];
    const vid = (p: Vec3) => {
      const found = vertices.find((v) => v.id.startsWith(`${d.id}-v`) && Math.abs(v.xyz[0]! - p[0]) < 1e-4 && Math.abs(v.xyz[1]! - p[1]) < 1e-4 && Math.abs(v.xyz[2]! - p[2]) < 1e-3);
      if (found) return found.id;
      const id = `${d.id}-v${ids.length + 1}`;
      ids.push(id);
      vertices.push({ id, xyz: p, source: "manual", locked: true });
      return id;
    };
    host.holes = [...(host.holes ?? []), g.cut.map(vid).reverse()];
    g.faces.forEach((f, i) => {
      const ring = f.points.map(vid);
      const normal = normalFrom(f.pitch, f.azimuth);
      const low = f.points.reduce((a, q) => (q[2] < a[2] ? q : a));
      const { u, v } = uvAxes(normal);
      const ground = Math.abs(ringArea(f.points.map((q) => [q[0], q[1]] as Vec2)));
      faces.push({
        id: `${d.id}-f${i + 1}`,
        label: `Dormer ${f.key}`,
        boundary: ring,
        plane: { normal, d: -(normal[0] * low[0] + normal[1] * low[1] + normal[2] * low[2]), pitch_deg: f.pitch, azimuth_deg: f.azimuth },
        uv_frame: { origin: low, u, v },
        area_m2: ground / Math.max(normal[2], 1e-3),
        ground_area_m2: ground,
        mountable: true,
        structure_id: host.structure_id ?? null,
        source: "manual",
        locked: true,
        dormer_id: d.id,
      });
      ring.forEach((a, k) => edges.push({ id: `${d.id}-e${edges.length}`, v: [a, ring[(k + 1) % ring.length]!], type: "unknown", type_source: "auto", faces: [`${d.id}-f${i + 1}`], length_m: 0, source: "manual", locked: true }));
    });
  }
}
